import { Subscription } from '../rpc'
import { GamepadConnectionEvent, GamepadSnapshot, GamepadApi, GamepadLatencyStats } from './GamepadApi'
import { WebGamepadReader } from './WebGamepadReader'
import { LatencyTracker } from './LatencyTracker'

/**
 * Mock/playground input: a local Tier-1 passthrough. When the browser exposes the
 * Web Gamepad API the dev surface reads real controllers; otherwise it reports
 * unsupported with an empty pad list.
 */
export class MockGamepadApi implements GamepadApi {
  private readonly reader = new WebGamepadReader()
  private readonly latency = new LatencyTracker({ windowSize: 120, enabled: false })
  private readonly connectedSubscribers = new Set<(event: GamepadConnectionEvent) => void>()
  private readonly disconnectedSubscribers = new Set<(event: GamepadConnectionEvent) => void>()
  private started = false

  /** No handshake on the local/mock tier — capability is known synchronously. */
  ready(): Promise<void> {
    return Promise.resolve()
  }

  isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof (navigator as Navigator).getGamepads === 'function'
    )
  }

  getGamepads(): GamepadSnapshot[] {
    return this.isSupported() ? this.reader.read() : []
  }

  onConnected(callback: (event: GamepadConnectionEvent) => void): Subscription {
    this.ensureStarted()
    this.connectedSubscribers.add(callback)
    return { unsubscribe: () => this.connectedSubscribers.delete(callback) }
  }

  onDisconnected(callback: (event: GamepadConnectionEvent) => void): Subscription {
    this.ensureStarted()
    this.disconnectedSubscribers.add(callback)
    return { unsubscribe: () => this.disconnectedSubscribers.delete(callback) }
  }

  readonly __debug = {
    getLatencyStats: (): GamepadLatencyStats => this.latency.getStats(),
  }

  private ensureStarted(): void {
    if (this.started || !this.isSupported()) return
    this.started = true
    this.reader.start(
      () => {},
      (event) => {
        for (const callback of [...this.connectedSubscribers]) callback(event)
      },
      (event) => {
        for (const callback of [...this.disconnectedSubscribers]) callback(event)
      },
    )
  }
}
